// Pre-need timeline (T-162): one row per step, one status word, at most one
// action. Pure, so the Details-tab section and the tests agree.
import type { Contract } from '../types';

export type StepState = 'done' | 'now' | 'wait' | 'pending';
export type StepAction =
  | 'confirm_certificate'
  | 'manage_certificate'
  | 'hold_package'
  | 'send_package'
  | 'toggle_registry';

export interface PreneedStep {
  key: string;
  label: string;
  state: StepState;
  /** The short word in the pill: a date, "Pending", "Held"… */
  pill: string;
  /** One line under the row. */
  note?: string;
  actions?: StepAction[];
}

export interface PreneedTimelineInput {
  region?: string | null;
  quoteSentAt?: string | null;
  contractsSentAt?: string | null;
  contractsSignedAt?: string | null;
  registered?: boolean;
  contract?: Partial<Contract> | null;
  now?: Date;
}

/** Days without a certificate before the row turns amber. */
export const CERTIFICATE_OVERDUE_DAYS = 21;

export function shortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function timeOf(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const daysBetween = (from: string, now: Date) =>
  Math.max(
    0,
    Math.floor((now.getTime() - new Date(from).getTime()) / 86400000)
  );

/** The one status word for the case (sidebar header, list pill). */
export function preneedStatusWord(
  c?: Partial<Contract> | null
): 'Active' | 'At TruStage' | null {
  if (!c) return null;
  if (c.trustage_status === 'active') return 'Active';
  if (c.trustage_status === 'sent') return 'At TruStage';
  return null;
}

/**
 * The list's pre-need pill once TruStage has the plan, or null to fall back
 * to Signed / Sent. Only for open pre-need cases.
 */
export function preneedStageBadge(
  record:
    | { has_preneed?: boolean | null; has_atneed?: boolean | null }
    | null
    | undefined,
  contract?: {
    trustage_status?: string | null;
    trustage_sent_at?: string | null;
  } | null
): {
  label: 'Active' | 'At TruStage';
  variant: 'success' | 'warning';
  since?: string | null;
} | null {
  if (!record?.has_preneed || record?.has_atneed) return null;
  const word = preneedStatusWord(contract as any);
  if (word === 'Active') return { label: 'Active', variant: 'success' };
  if (word === 'At TruStage')
    return {
      label: 'At TruStage',
      variant: 'warning',
      since: contract?.trustage_sent_at,
    };
  return null;
}

export function preneedSteps(input: PreneedTimelineInput): PreneedStep[] {
  const now = input.now ?? new Date();
  const c = input.contract ?? {};
  const steps: PreneedStep[] = [];

  steps.push({
    key: 'quote',
    label: 'Quote sent',
    state: input.quoteSentAt ? 'done' : 'pending',
    pill: shortDate(input.quoteSentAt) || 'Pending',
  });

  const sentAt = input.contractsSentAt;
  const signedAt = input.contractsSignedAt;
  steps.push({
    key: 'contract',
    label: 'Contract sent',
    state: sentAt ? 'done' : 'pending',
    pill: shortDate(sentAt) || 'Pending',
  });
  steps.push({
    key: 'signed',
    label: 'Signed',
    state: signedAt ? 'done' : sentAt ? 'now' : 'pending',
    pill: signedAt
      ? shortDate(signedAt)
      : sentAt
      ? `Not signed · ${daysBetween(sentAt, now)}d`
      : 'Pending',
  });

  const active = c.trustage_status === 'active';
  const hasPdf = !!c.certificate_received_at;

  // Sent to TruStage
  if (c.trustage_sent_at) {
    const waiting = !hasPdf && !active;
    const days = daysBetween(c.trustage_sent_at, now);
    steps.push({
      key: 'trustage',
      label: 'Sent to TruStage',
      state: waiting && days >= CERTIFICATE_OVERDUE_DAYS ? 'wait' : 'done',
      pill: shortDate(c.trustage_sent_at),
      note: waiting
        ? `Waiting for the certificate · ${days} day${days === 1 ? '' : 's'}`
        : undefined,
    });
  } else if (active) {
    // Sent by hand before the automatic forward existed (T-212).
    steps.push({
      key: 'trustage',
      label: 'Sent to TruStage',
      state: 'done',
      pill: 'Yes',
    });
  } else {
    steps.push({
      key: 'trustage',
      label: 'Sent to TruStage',
      state: 'pending',
      pill:
        signedAt && c.trustage_forward === false
          ? 'Not sent (box unticked)'
          : 'Pending',
    });
  }

  // Certificate received · Active. Staff can add the number, the PDF or the
  // Active status by hand once the plan is signed (T-212).
  const manualOk = !!(signedAt || c.trustage_sent_at || active);
  const policy = c.trustage_certificate_number;
  const setBy = c.trustage_certificate_number_set_by
    ? ` · set by ${c.trustage_certificate_number_set_by}, ${shortDate(
        c.trustage_certificate_number_set_at
      )}`
    : '';
  if (hasPdf) {
    steps.push({
      key: 'certificate',
      label: 'Certificate received · Active',
      state: 'done',
      pill: shortDate(c.certificate_received_at),
      note: policy ? `Certificate ${policy}${setBy}` : undefined,
      actions: ['manage_certificate'],
    });
  } else if (c.certificate_candidate?.attachment_id) {
    steps.push({
      key: 'certificate',
      label: 'Certificate received?',
      state: 'wait',
      pill: 'Confirm match',
      note: `${c.certificate_candidate.filename || 'A PDF'} came in a ${
        c.certificate_candidate.reason === 'forwarded'
          ? 'forwarded email'
          : 'separate email'
      }. Is it this plan's TruStage certificate?`,
      actions: ['confirm_certificate'],
    });
  } else if (active) {
    steps.push({
      key: 'certificate',
      label: 'Active · no certificate PDF',
      state: 'now',
      pill: shortDate(c.trustage_activated_at) || 'Active',
      note: `Policy ${policy ?? '—'}${setBy}. Marked Active${
        c.trustage_activated_by ? ` by ${c.trustage_activated_by}` : ''
      }. Upload the certificate when TruStage sends it.`,
      actions: ['manage_certificate'],
    });
  } else {
    steps.push({
      key: 'certificate',
      label: 'Certificate received',
      state: 'pending',
      pill: 'Pending',
      note: policy ? `Policy ${policy}${setBy}` : undefined,
      actions: manualOk ? ['manage_certificate'] : undefined,
    });
  }

  // Registered (Québec only; the existing Registry checkbox)
  if (input.region !== 'ontario') {
    steps.push({
      key: 'registry',
      label: 'Registered',
      state: input.registered ? 'done' : 'pending',
      pill: input.registered ? 'Yes' : 'Not yet',
      actions: ['toggle_registry'],
    });
  }

  // Confirmation package: it includes the certificate, so it waits for the PDF.
  if (c.confirmation_package_sent_at) {
    steps.push({
      key: 'package',
      label: 'Confirmation package sent',
      state: 'done',
      pill: shortDate(c.confirmation_package_sent_at),
    });
  } else if (c.confirmation_package_scheduled_at) {
    steps.push({
      key: 'package',
      label: 'Confirmation package',
      state: 'now',
      pill: `Scheduled ${timeOf(c.confirmation_package_scheduled_at)}`,
      note: `Will be sent at ${timeOf(c.confirmation_package_scheduled_at)}.`,
      actions: ['hold_package'],
    });
  } else if (c.confirmation_package_held_at) {
    steps.push({
      key: 'package',
      label: 'Confirmation package',
      state: 'wait',
      pill: 'Held',
      note: `Held${
        c.confirmation_package_held_by
          ? ` by ${c.confirmation_package_held_by}`
          : ''
      }, ${shortDate(c.confirmation_package_held_at)}.`,
      actions: ['send_package'],
    });
  } else if (c.confirmation_package_shadow_at) {
    steps.push({
      key: 'package',
      label: 'Confirmation package',
      state: 'now',
      pill: 'Shadow',
      note: 'Shadow mode: it would have been sent. Nothing was emailed.',
      actions: ['send_package'],
    });
  } else {
    steps.push({
      key: 'package',
      label: 'Confirmation package',
      state: 'pending',
      pill: 'Pending',
      note:
        active && !hasPdf ? 'Waits for the certificate PDF.' : undefined,
      actions: hasPdf ? ['send_package'] : undefined,
    });
  }

  return steps;
}
